/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import { ICartlinesViewProps, ICartViewProps, IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart';
import { ICartResources } from '../definition-extensions/cart.ext.props.autogenerated';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/dist/types/modules/cart/components/cart-line-items-promotion';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    ITelemetryContent,
    Node,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICustomerAttributeData } from './cart.data';
import getAttributeObject from '../utilities/buybox/get-address-attribute-object';
import { WithDocumentNamePropertyType } from '../utilities/propertyTypeEnum';
import { updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

const FOLIO_NUMBERS_STORAGE_NAME = 'FOLIO_NUMBERS';

interface IFolioNumber {
    name: string;
    folioNumber: string;
    folioNumberStatus: boolean;
    validationMessage: string;
    lineId: string;
    isAddress: boolean;
}

const CartView: React.FC<ICartViewProps & ICartProps<ICustomerAttributeData>> = (
    props: ICartViewProps & ICartProps<ICustomerAttributeData>
) => {
    const { downloadUnavailableHeader, downloadUnavailableBody, downloadUnavailableDate, showInfoBox } = props.config;
    const {
        closeLabel,
        checkoutButtonTitle,
        guestCheckoutButtonTitle,
        loadingLabel,
        refreshAndTryAgainMessage,
        folioNumberText
    } = props.resources;
    const [folioNumbers, setFolioNumbers] = React.useState<IFolioNumber[]>([]);
    const { folioNumberValueAttribute, folioNumberActivationStatus, encroachmentProducts } = props.context.app.config;
    const isCartEmpty = props.cart && props.cart.cart.CartLines && props.cart.cart.CartLines.length === 0;
    const { isAuthenticated } = props.context.request.user;

    const getFolioStatus = () => {
        const customerInformation = props.data.customerInformation.result;
        const attributeDefinitions = props.data.attributeDefinitions.result;
        const folioActivation = customerInformation?.Attributes?.find(x => x.Name === folioNumberActivationStatus)?.AttributeValue
            ?.StringValue;
        if (folioActivation === undefined) {
            if (customerInformation?.IsB2b === false) {
                return false;
            } else {
                return (
                    attributeDefinitions?.find(x => x.Name === folioNumberActivationStatus)?.DefaultValue?.StringValue?.toLowerCase() ===
                    'true'
                );
            }
        } else {
            return (
                customerInformation?.Attributes?.find(
                    x => x.Name === folioNumberActivationStatus
                )?.AttributeValue?.StringValue?.toLowerCase() === 'true'
            );
        }
    };
    const [showFolioNumber] = React.useState(getFolioStatus() && isAuthenticated);
    const [disableCheckout, setDisableCheckout] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const itemsCount = props.data.cart.result?.cart.CartLines?.length;
    const [showAlert, setShowAlert] = React.useState(true);
    const [isError, setIsError] = React.useState(false);
    const { itemsInCurrentOrderLabel } = props.resources;
    const { disableGuestCheckout } = props.config;
    React.useEffect(() => {
        async function addresshandling() {
            const storageName = `${props.context.request.user.customerAccountNumber}-${FOLIO_NUMBERS_STORAGE_NAME}`;
            const newFolioNumbers: IFolioNumber[] = [];
            for (let index = 0; index < props.cart?.cart.CartLines?.length!; index++) {
                const line = props.cart?.cart.CartLines![index];
                const lineId = line?.LineId || '';
                //@ts-ignore
                const folio: string = line.AttributeValues?.find(attr => attr.Name === folioNumberValueAttribute)?.TextValue || '';
                const result = await getAttributeValuesAsync(
                    { callerContext: props.context.actionContext, queryResultSettings: {} },
                    line?.ProductId!,
                    props.context.request.apiSettings.channelId,
                    0
                );
                const checkEncroachment =
                    result?.find(attr => attr.Name?.toLowerCase() === encroachmentProducts?.toLowerCase())?.BooleanValue || false;
                const folioArr = folio.split('||');
                const folioValue = checkEncroachment ? (folioArr.length > 0 ? folioArr[1] : folioArr[0]) : folio;
                const folioObj = {
                    name: `${folioNumberText}${lineId}`,
                    folioNumber: folioValue,
                    lineId: lineId,
                    folioNumberStatus: isValidFolioNumber(folioValue),
                    validationMessage: '',
                    isAddress: checkEncroachment
                };
                const localStorageFolios: IFolioNumber[] = JSON.parse(localStorage.getItem(storageName) || '[]');
                newFolioNumbers.push(
                    folioNumbers.find(x => x.lineId === folioObj.lineId) ||
                        localStorageFolios.find(x => x.lineId === folioObj.lineId) ||
                        folioObj
                );
            }
            localStorage.setItem(storageName, JSON.stringify(newFolioNumbers));
            const isDisabled = newFolioNumbers.some(folioNumber => folioNumber.folioNumberStatus === false);
            setDisableCheckout(isDisabled);
            setFolioNumbers(newFolioNumbers);
        }
        addresshandling();
    }, [props.cart?.cart.CartLines?.length]);

    const signInUrl = props.context.request.user.signInUrl;
    const guestCheckoutUrl = getUrlSync('checkout', props.context.actionContext) || '';
    const checkoutURL = _getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
    function _getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }
        const absoluteUrlRegExp = new RegExp('^(?:[a-z]+:)?//', 'i');
        const cartUrl = getUrlSync('cart', props.context.actionContext)!;
        const isAbsoluteUrl = absoluteUrlRegExp.test(cartUrl);
        let returnURL = cartUrl;
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${cartUrl}`;
        }
        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    }

    const isValidFolioNumber = (folioNumber: string): boolean => {
        return (
            folioNumber?.length <= parseInt(props.config.folioNumberMaxLength!) &&
            folioNumber?.length >= parseInt(props.config.folioNumberMinLength!)
        );
    };

    const onCheckoutClick = async (url: string) => {
        setIsLoading(true);
        try {
            if (showFolioNumber || folioNumbers.some(folio => folio.isAddress)) {
                const cartLines = props.cart?.cart.CartLines || [];
                folioNumbers.forEach(folio => {
                    const line = cartLines.find(line => line.LineId === folio.lineId);
                    if (line) {
                        const folioValue = folio.isAddress ? `MunicipalAddress||${folio.folioNumber}` : folio.folioNumber;
                        const newAttrs = line.AttributeValues?.filter(attr => attr.Name !== folioNumberValueAttribute) || [];
                        newAttrs.push(getAttributeObject(folioNumberValueAttribute, folioValue));
                        line.AttributeValues = newAttrs;
                    }
                });
                props.cart && (await updateCartLinesAsync({ callerContext: props.context.actionContext }, props.cart.cart.Id, cartLines));
                await props.cart?.refreshCart({});
            }
            window.location.assign(url);
        } catch {
            setIsLoading(false);
            setIsError(true);
        }
    };
    const isDisabledCheckoutButton = () => {
        return isAuthenticated
            ? showFolioNumber
                ? disableCheckout || isError
                : folioNumbers.some(folio => folio.isAddress && !folio.folioNumberStatus)
            : false;
    };
    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            <div className='back-shop'>{props.backToShoppingButton}</div>
            {props.title}
            {showInfoBox && showAlert && (
                <div className='cui alert-banner global information'>
                    <div className='alert-header'>
                        <span className='alert-icon cicon-exclamation-circle'></span>
                        <h2 className='alert-title'>{downloadUnavailableHeader}</h2>
                        <button className='alert-action-btn cui btn-md' onClick={e => setShowAlert(false)}>
                            <span className='text'>{closeLabel}</span>
                            <span className='cicon-close-thin-large right'></span>
                        </button>
                    </div>
                    <div className='alert-body'>
                        <p>{downloadUnavailableBody}</p>
                        <p>{downloadUnavailableDate}</p>
                    </div>
                </div>
            )}
            {props.promotionOptions && _renderPromotions(props.promotionOptions)}
            {props.multiplePickUpEnabled ? (
                _renderCartLinesGroup(
                    props,
                    props.resources,
                    showFolioNumber,
                    folioNumbers,
                    setFolioNumbers,
                    setDisableCheckout,
                    isValidFolioNumber
                )
            ) : (
                <Node {...props.CartlinesWrapper}>
                    {_renderCartlines(
                        props,
                        props.cartlines,
                        props.resources,
                        props.storeSelector,
                        props.waitingComponent,
                        props.cartLoadingStatus,
                        props.cartDataResult,
                        folioNumbers,
                        setFolioNumbers,
                        setDisableCheckout,
                        isValidFolioNumber,
                        props.telemetryContent,
                        props.multiplePickUpEnabled,
                        props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode,
                        showFolioNumber
                    )}
                </Node>
            )}
            {props.orderSummaryHeading && (
                <Node {...props.OrderSummaryWrapper}>
                    {props.orderSummaryHeading}
                    {/* VSI-Customization -- START Display count of items in cart  */}
                    {
                        <Node className='msc-order-summary__item-count' tag='p'>
                            {itemsInCurrentOrderLabel} {itemsCount}
                        </Node>
                    }
                    {/* VSI-Customization -- END  */}
                    {props.cart?.hasInvoiceLine
                        ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                        : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                    {_renderErrorBlock(props.OrderSummaryErrors)}
                    {isError && <span className='cui alert-small error'>{refreshAndTryAgainMessage}</span>}
                    <div className='btn-box'>
                        {!isCartEmpty ? (
                            <Button
                                onClick={() => onCheckoutClick(checkoutURL)}
                                disabled={isDisabledCheckoutButton()}
                                className='msc-cart__btn-checkout'
                                title={checkoutButtonTitle}
                            >
                                {checkoutButtonTitle}
                            </Button>
                        ) : null}
                        {!disableGuestCheckout && !isCartEmpty && !isAuthenticated ? (
                            <Button
                                onClick={() => onCheckoutClick(guestCheckoutUrl)}
                                disabled={isDisabledCheckoutButton()}
                                className='msc-cart__btn-guestcheckout'
                                title={guestCheckoutButtonTitle}
                            >
                                {guestCheckoutButtonTitle}
                            </Button>
                        ) : null}
                        {isLoading ? (
                            <div className='spinner-wrap'>
                                <div className='spinner-border' role='status'>
                                    <span className='sr-only'>{loadingLabel}</span>
                                </div>
                                <p>{loadingLabel}</p>
                            </div>
                        ) : null}
                    </div>
                </Node>
            )}
        </div>
    );
};

const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <React.Fragment>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </React.Fragment>
    );
};

const _renderCartlines = (
    props: ICartViewProps & ICartProps<{}>,
    cartLines: ICartlinesViewProps[] | undefined,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    waitingComponent: React.ReactNode,
    cartLoadingStatus: string,
    cartDataResult: boolean,
    folioNumbers: IFolioNumber[],
    setFolioNumbers: React.Dispatch<React.SetStateAction<IFolioNumber[]>>,
    setDisableCheckout: React.Dispatch<React.SetStateAction<boolean>>,
    isValidFolioNumber: (str: string) => boolean,
    telemetryContent?: ITelemetryContent,
    mulitplePickUp?: boolean,
    emailDeliveryModeCode?: string,
    showFolioNumber?: boolean
): JSX.Element[] | JSX.Element => {
    const {
        emptyCartText,
        folioNumberRequired,
        folioNumberInvalid,
        municipalAddressRequired,
        municipalAddressInvalid,
        folioNumberLabel,
        folioNumberText,
        searchKeyLabel,
        documentNameLabel,
        addressValueLabel,
        folioNumberRequiredLabel
    } = resources;
    const { searchKeyValueAttribute, documentAttribute, searchKeyNameAttribute } = props.context?.request.app.config;
    const onInputBlur = () => {
        const storageName = `${props.context.request.user.customerAccountNumber}-${FOLIO_NUMBERS_STORAGE_NAME}`;
        localStorage.setItem(storageName, JSON.stringify(folioNumbers));
    };
    const onFolioNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;
        const folio = folioNumbers.find(folio => folio.name === name)!;
        folio.folioNumber = value;
        if (isValidFolioNumber(value)) {
            folio.validationMessage = '';
            folio.folioNumberStatus = true;
        } else {
            folio.validationMessage =
                value.length === 0
                    ? folio.isAddress
                        ? municipalAddressRequired
                        : folioNumberRequired
                    : folio.isAddress
                    ? municipalAddressInvalid
                    : folioNumberInvalid;
            folio.folioNumberStatus = false;
        }
        setFolioNumbers([...folioNumbers]);
        const checkFolio = folioNumbers.find(f => f.folioNumberStatus === false);
        if (checkFolio?.folioNumberStatus === false) {
            setDisableCheckout(true);
        } else {
            setDisableCheckout(false);
        }
    };

    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map((cartLine, index) => {
            const attributeValue: string =
                //@ts-ignore
                cartLine.data?.cartline.AttributeValues?.find(line => line.Name === searchKeyValueAttribute)?.TextValue || '';
            const attributeDocument: string =
                //@ts-ignore
                cartLine.data?.cartline.AttributeValues?.find(line => line.Name === documentAttribute)?.TextValue || '';
            const propertyType: string =
                //@ts-ignore
                cartLine.data?.cartline.AttributeValues?.find(line => line.Name === searchKeyNameAttribute)?.TextValue || '';
            const name = `${folioNumberText}${cartLine.cartlineId!}`;
            const errorId = `${folioNumberText}${cartLine.cartlineId!}Er`;
            const folioNumber = folioNumbers.find(folio => folio.name === name);
            const isAddress = folioNumber?.isAddress;
            const searchKey: string = attributeValue.includes('||')
                ? attributeValue
                      .split('||')
                      .map((val: string) => val?.trim().toUpperCase())
                      .join(' ')
                : attributeValue.toUpperCase();
            const documentName = propertyType in WithDocumentNamePropertyType ? attributeDocument.split('||')[1]?.trim() : undefined;

            return (
                <div className='msc-cart-lines-item' key={index}>
                    {cartLine.cartline}
                    {searchKey && <div className='search-key'>{`${searchKeyLabel} ${searchKey}`}</div>}
                    {documentName && <div className='search-key'>{`${documentNameLabel} ${documentName}`}</div>}
                    {mulitplePickUp ? null : _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                    {showFolioNumber || isAddress ? (
                        <div className='folio-field form-group'>
                            <label htmlFor={name}>
                                {isAddress ? addressValueLabel : folioNumberLabel}
                                <span className='starick'>{folioNumberRequiredLabel}</span>
                            </label>
                            <input
                                className={'form-field'}
                                name={name}
                                id={name}
                                aria-describedby={errorId}
                                value={folioNumber?.folioNumber}
                                onChange={onFolioNumberChange}
                                maxLength={parseInt(props.config.folioNumberMaxLength!)}
                                minLength={parseInt(props.config.folioNumberMinLength!)}
                                onBlur={onInputBlur}
                            />
                            <span id={errorId} className='cui alert-small error'>
                                {folioNumber?.validationMessage != '' ? folioNumber?.validationMessage : null}
                            </span>
                        </div>
                    ) : null}
                    {mulitplePickUp ? (
                        <Node className='msc-cart-line-group__extra-actions'>
                            {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                            {cartLine.remove}
                        </Node>
                    ) : cartLine.addToOrderTemplate ? (
                        <Node className='msc-cart-line__extra-actions'>{cartLine.remove}</Node>
                    ) : (
                        <>{cartLine.remove}</>
                    )}
                </div>
            );
        });
    } else {
        /* VSI-Customization -- START Display continue shopping button with different label/text  */
        const backToShoppingUrl = getUrlSync('home', props.context.actionContext) || '';
        const { continueShoppingBrowserOurProducts } = resources;
        const continueShoppingBrowserOurProductsButton = (
            <Button className='msc-cart__btn-backtoshopping' href={backToShoppingUrl} title={continueShoppingBrowserOurProducts}>
                {continueShoppingBrowserOurProducts}
            </Button>
        );
        /* VSI-Customization -- END  */
        return cartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>{emptyCartText}</p>
                {continueShoppingBrowserOurProductsButton}
            </div>
        ) : (
            <>{waitingComponent}</>
        );
    }
};

const _renderBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const {
        shipInsteadDisplayText,
        shipToAddressDisplayText,
        pickItUpDisplayText,
        pickUpAtStoreWithLocationText,
        changeStoreDisplayText
    } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
    };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
    };
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? (
                    <span className='pick-up'>{pickUpAtStoreWithLocationText}</span>
                ) : (
                    <span className='ship'>{shipToAddressDisplayText}</span>
                )}
            </div>
            {isBopisSelected && (
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button className='msc-cart-line__bopis-changestore' onClick={changeStore}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            )}
            <Button className='msc-cart-line__bopis-btn' {...puckUpinStoreAttribute} onClick={toggleBopis}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </Node>
    );
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const _renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps & ICartProps<{}>
): JSX.Element | null => {
    if (!orderSummaryLines) {
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

const _renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    OrderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

const _renderCartLinesGroup = (
    props: ICartViewProps & ICartProps<{}>,
    resources: ICartResources,
    showFolioNumber: boolean,
    folioNumbers: IFolioNumber[],
    setFolioNumbers: React.Dispatch<React.SetStateAction<IFolioNumber[]>>,
    setDisableCheckout: React.Dispatch<React.SetStateAction<boolean>>,
    isValidFolioNumber: (str: string) => boolean
): JSX.Element | undefined => {
    const { emptyCartText } = resources;
    if (props.cartLinesGroup && props.cartLinesGroup.length > 0) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup?.map(cartlines => {
                    // tslint:disable-next-line:jsx-key
                    return (
                        <div className='msc-cart-lines-group-wraper'>
                            {_renderCartLinesGroupHeader(cartlines[0], resources, _countItems(cartlines))}
                            {_renderCartlines(
                                props,
                                cartlines,
                                props.resources,
                                props.storeSelector,
                                props.waitingComponent,
                                props.cartLoadingStatus,
                                props.cartDataResult,
                                folioNumbers,
                                setFolioNumbers,
                                setDisableCheckout,
                                isValidFolioNumber,
                                props.telemetryContent,
                                props.multiplePickUpEnabled,
                                props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode,
                                showFolioNumber
                            )}
                        </div>
                    );
                })}
            </div>
        );
    } else {
        return props.cartDataResult ? (
            <div className='msc-cartline-wraper'>
                <div className='msc-cart__empty-cart'>
                    <p className='msc-cart-line'>{emptyCartText}</p>
                    {props.backToShoppingButton}
                </div>
            </div>
        ) : (
            <>{props.waitingComponent}</>
        );
    }
};

const _renderCartLinesGroupHeader = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine?.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText } = resources;
    return (
        <React.Fragment>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-pickup-icon' />
                ) : (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-ship-icon' />
                )}
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-pickup'>{pickUpText}</p>
                ) : (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-ship'>{shippingText}</p>
                )}
                {_renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </React.Fragment>
    );
};

const _renderGroupBOPISBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;
    const toggleBopis = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
    };
    const changeStore = (event: React.MouseEvent<HTMLElement>) => {
        return cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
    };
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <React.Fragment>
            {isBopisSelected ? (
                <Button className='msc-cart-line__remove-item msc-btn' onClick={changeStore}>
                    {changeStoreDisplayText}
                </Button>
            ) : null}
            <Button className='msc-cart-line__remove-item msc-btn' {...puckUpinStoreAttribute} onClick={toggleBopis}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </React.Fragment>
    );
};

const _countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
    countItem = cartLine.reduce((count, item) => {
        return count + (item.data?.cartline.Quantity || 0);
    }, 0);
    return countItem;
};

const _renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine?.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const suffix = count > 1 ? itemsLabel : itemLabel;
    return (
        <React.Fragment>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                        {cartLine?.pickUpInStore?.deliveryOption}
                        {', '}
                        {cartLine?.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
                {
                    <p className='msc-cart-lines-group-wraper__bopis-heading-title-ct'>
                        ({count} {suffix})
                    </p>
                }
            </Node>
        </React.Fragment>
    );
};

export default CartView;
